import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DollarSign, Receipt, TrendingUp, Scale } from 'lucide-react';
import apiClient from '@/services/api';
import type { SalesSummaryReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { paymentClass } from '@/components/reports/paymentClass';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

const num = (v: number | string | null | undefined) => Number(v) || 0;

interface Line {
  label: string;
  /** null → value needs POS-only data not synced to the server (rendered "n/a"). */
  amount: number | null;
  /** Subtotal line: bold with a dashed rule above. */
  total?: boolean;
}

/**
 * Sales Balance — mirrors the POS "Sales Summary / Sales Balance" statement
 * (RECEIPTS SUMMARY → PAYMENT METHOD → CASH SUMMARY) using the same line
 * labels and running calculations. Lines that depend on POS-only data not
 * synced to the server (gift certs, pay-ins, drawer pulls, payouts, cash-on-
 * hand, taxable split, tips paid) are shown as "n/a".
 */
export default function SalesBalanceReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesSummaryReport>({
    queryKey: ['sales-summary', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesSummary(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const t = data?.totals;
  // Synced figures.
  const discount = num(t?.discount);
  const tax = num(t?.tax);
  const service = num(t?.serviceCharge);
  const tip = num(t?.tip);
  const delivery = num(t?.deliveryCharge);
  const subtotalRaw = num(t?.subtotal);       // POS subtotal: GROSS, pre-discount
  const totalAmt = num(t?.total);
  const discountedBase = subtotalRaw - discount;  // amount actually charged (post-discount)
  // Detect tax-inclusive pricing (UK VAT) against the discounted base: if it
  // already embeds the tax, adding Sales Taxes on top would double-count.
  // Choose the model that reconciles to the real total.
  const inclusiveTax =
    Math.abs((discountedBase + service + delivery) - totalAmt) <=
    Math.abs((discountedBase + tax + service + delivery) - totalAmt);
  // Net Sales = post-discount, ex-VAT — so Net + Sales Taxes ties back to the total like the POS.
  const netSales = inclusiveTax ? discountedBase - tax : discountedBase;
  const grossSales = netSales + discount;     // gross taxable (pre-discount) line

  let cash = 0, card = 0, other = 0;
  for (const m of data?.byPaymentMethod ?? []) {
    const amt = num(m.amount);
    const cls = paymentClass(m.method);
    if (cls === 'cash') cash += amt;
    else if (cls === 'card') card += amt;
    else other += amt;
  }

  const totalRevenues = netSales + tax + service + delivery;
  const grossReceipts = totalRevenues + tip;   // + gift cert sold (n/a) + pay ins (n/a)
  const receiptDiff = grossReceipts - cash - card - other;

  const receipts: Line[] = [
    { label: '  Gross Taxable Sales', amount: grossSales },
    { label: '+ Gross Non-Taxable Sales', amount: 0 },
    { label: '- Discounts', amount: discount },
    { label: '= Net Sales', amount: netSales, total: true },
    { label: '  Net Sales', amount: netSales },
    { label: '+ Sales Taxes', amount: tax },
    { label: '+ Service Charge', amount: service },
    { label: '+ Delivery Charge', amount: delivery },
    { label: '= Total Revenues', amount: totalRevenues, total: true },
    { label: '+ Gift Certificates Sold', amount: null },
    { label: '+ Pay-Ins', amount: null },
    { label: '+ Charged Tips', amount: tip },
    { label: '= Gross Receipts', amount: grossReceipts, total: true },
  ];
  const payment: Line[] = [
    { label: '- Cash Receipts', amount: cash },
    { label: '- Credit Card Receipts', amount: card },
    { label: '- Accounts Receivable / Other Receipts', amount: other },
    { label: '- Gift Certificate Returns', amount: null },
    { label: '+ Gift Certificate Change', amount: null },
    { label: '+ Cash Back', amount: null },
    { label: '= Receipt Difference', amount: receiptDiff, total: true },
  ];
  const cashSummary: Line[] = [
    { label: '  Cash Receipts', amount: cash },
    { label: '- Gross Tips Paid', amount: null },
    { label: '+ Tips Discount', amount: null },
    { label: '- Cash Pay-Out', amount: null },
    { label: '- Gift Certificate Change', amount: null },
    { label: '- Cash Back', amount: null },
    { label: '= Cash Accountable', amount: null, total: true },
    { label: '- Drawer Pulls', amount: null },
    { label: '- Cash On Hand (Current)', amount: null },
    { label: '+ Cash On Hand (Previous)', amount: null },
    { label: '= Over (+) / Short (-)', amount: null, total: true },
  ];

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const cell = (l: Line) => (l.amount === null ? 'n/a' : formatCurrency(l.amount));
  const toRows = (lines: Line[]) => lines.map((l) => [l.label.trim(), cell(l)]);
  const sections = (): ReportSection[] => [
    { heading: 'Receipts Summary', headers: ['Line', 'Amount'], rows: toRows(receipts) },
    { heading: 'Payment Method', headers: ['Line', 'Amount'], rows: toRows(payment) },
    { heading: 'Cash Summary (needs POS drawer data — not synced)', headers: ['Line', 'Amount'], rows: toRows(cashSummary) },
  ];
  const kpis = (): [string, string][] => [
    ['Net Sales', formatCurrency(netSales)],
    ['Total Revenues', formatCurrency(totalRevenues)],
    ['Gross Receipts', formatCurrency(grossReceipts)],
    ['Receipt Diff', formatCurrency(receiptDiff)],
  ];

  const Statement = ({ title, note, lines }: { title: string; note?: string; lines: Line[] }) => (
    <div className="card"><div className="card-body">
      <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-1 text-center">{title}</h3>
      {note && <p className="text-xs text-gray-400 mb-3 text-center">{note}</p>}
      <div className="max-w-md mx-auto font-mono text-sm">
        {lines.map((l, i) => (
          <div
            key={i}
            className={`flex justify-between py-1 ${l.total ? 'border-t border-dashed border-gray-400 font-semibold text-gray-900' : 'text-gray-600'}`}
          >
            <span className="whitespace-pre">{l.label}</span>
            <span className={l.amount === null ? 'text-gray-400 italic' : ''}>{cell(l)}</span>
          </div>
        ))}
      </div>
    </div></div>
  );

  return (
    <ReportFrame
      title="Sales Balance"
      description="POS-style sales summary statement: receipts, payment reconciliation and cash summary."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={!data || (t?.orders ?? 0) === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`sales-balance-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Sales Balance', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Sales Balance Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Net Sales" value={formatCurrency(netSales)} icon={DollarSign} palette="blue" />
        <KPICard label="Total Revenues" value={formatCurrency(totalRevenues)} icon={TrendingUp} palette="green" />
        <KPICard label="Gross Receipts" value={formatCurrency(grossReceipts)} icon={Receipt} palette="purple" />
        <KPICard label="Receipt Diff" value={formatCurrency(receiptDiff)} icon={Scale} palette="red" />
      </div>

      <Statement
        title="Receipts Summary"
        note={inclusiveTax ? 'VAT-inclusive pricing detected — Net Sales shown ex-VAT so Net + Sales Taxes reconciles to Total Revenues.' : undefined}
        lines={receipts}
      />
      <Statement title="Payment Method" lines={payment} />
      <Statement
        title="Cash Summary"
        note="Cash reconciliation lines need POS drawer / payout / cash-on-hand data, which is not synced to the management server."
        lines={cashSummary}
      />
    </ReportFrame>
  );
}
